/**
 * Turns Server-sent template params into display strings.
 *
 * The Server sends `copyKey` + `params` and never rendered prose, so the
 * client is what knows a Money object should become "$19.99", a basis-point
 * rate "24.99%", and a category id its localized name.
 */
import type { BankConnection, CategoryId, Money, Recommendation, Progress } from '@/generated/types'
import { t, tServer, type CopyKey, type CopyParams } from './copy'
import { formatBasisPoints, formatDate, formatMonth, formatMoney, formatPercent } from './format'

const isMoney = (value: unknown): value is Money =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Money).amount === 'number' &&
  typeof (value as Money).currency === 'string'

const isIsoDate = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)

const isMonthKey = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)

/** Keys whose numeric value is a ratio (0-1) rather than a count. */
const RATIO_KEYS = new Set(['percent', 'utilization', 'savingsRate'])
/** Keys whose numeric value is basis points. */
const BASIS_POINT_KEYS = new Set(['rate', 'apr', 'aprBasisPoints'])

export function displayParams(params: Record<string, unknown> | undefined): CopyParams {
  const out: CopyParams = {}
  for (const [key, value] of Object.entries(params ?? {})) {
    if (isMoney(value)) {
      out[key] = formatMoney(value, { absolute: true })
    } else if (typeof value === 'number' && RATIO_KEYS.has(key)) {
      out[key] = formatPercent(value)
    } else if (typeof value === 'number' && BASIS_POINT_KEYS.has(key)) {
      out[key] = formatBasisPoints(value)
    } else if (key === 'cadence' && typeof value === 'string') {
      out[key] = t(`recurring.row.cadence.${value}` as CopyKey)
    } else if (key === 'category' && typeof value === 'string') {
      out[key] = categoryName(value as CategoryId)
    } else if (key === 'band' && typeof value === 'string') {
      out[key] = tServer(`healthScore.band.${value}`)
    } else if (isIsoDate(value)) {
      out[key] = formatDate(value)
    } else if (isMonthKey(value)) {
      out[key] = formatMonth(value)
    } else if (typeof value === 'number' || typeof value === 'string') {
      out[key] = value
    }
  }
  return out
}

export function categoryName(category: CategoryId): string {
  return t(`category.${category}` as CopyKey)
}

export function recommendationTitle(rec: Recommendation): string {
  return tServer(`${rec.copyKey}.title`, displayParams(rec.params))
}

export function recommendationWhy(rec: Recommendation): string {
  return tServer(`${rec.copyKey}.why`, displayParams(rec.params))
}

export function milestoneTitle(milestone: Progress['milestones'][number]): string {
  const params = displayParams(milestone.params)
  // recommendationDone carries the recommendation's own copyKey as `title`.
  if (typeof milestone.params.title === 'string' && milestone.params.title.startsWith('recommendation.')) {
    params.title = tServer(`${milestone.params.title}.title`, params)
  }
  return tServer(milestone.copyKey, params)
}

export function accountTypeName(type: string): string {
  return t(`accounts.type.${type}` as CopyKey)
}

export function feeKindName(kind: string | null | undefined): string {
  return t(`fees.kind.${kind ?? 'other'}` as CopyKey)
}

export function cadenceName(cadence: string): string {
  return t(`recurring.row.cadence.${cadence}` as CopyKey)
}

export function effortName(effort: 'low' | 'medium' | 'high'): string {
  return t(`recommendations.effort.${effort}` as CopyKey)
}

/**
 * Connection status → badge. `active` reads as "Connected", so the copy key is
 * `.ok` rather than `.active` — the one place the badge key is not the enum
 * value. `disconnected` has no key because bank-connection.schema.json says
 * those rows are never listed.
 */
const CONNECTION_BADGE: Record<BankConnection['status'], CopyKey | null> = {
  active: 'connectedAccounts.badge.ok',
  syncing: 'connectedAccounts.badge.syncing',
  reauthRequired: 'connectedAccounts.badge.reauthRequired',
  error: 'connectedAccounts.badge.error',
  disconnected: null,
}

export function connectionStatusName(status: BankConnection['status']): string | null {
  const key = CONNECTION_BADGE[status]
  return key ? t(key) : null
}
